import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";

import * as amplitude from "@amplitude/analytics-browser";

import {
  setMechanicData,
  selectIsAuthenticated,
  selectMechanicId,
} from "./redux/auth";
import { setCart } from "./redux/cart";
import { setSkuCategories } from "./redux/sku";

import { useScrollToTop } from "./hooks/useScrollToTop";

import { getMechanicDetails } from "./services/mechanic";
import { getCart } from "./services/cart";
import { getSkuCategories } from "./services/sku";

import {
  mechanicDataMapper,
  skuCategoriesMapper,
  cartDataMapper,
} from "./helpers/dataMapper";

import { APP_PATHS } from "./constants/paths";
import { AMPLITUDE_API_KEY } from "./axios/axios";

import Layout from "./components/Layout/Layout";
import Router from "./components/Router/Router";
import Loading from "./components/UI/Loading/Loading";

import classes from "./App.module.css";

const App = () => {
  const isAuthenticated = useSelector(selectIsAuthenticated);

  const [isInitDataLoading, setIsInitDataLoading] = useState(
    isAuthenticated ? true : false
  );

  const userId = useSelector(selectMechanicId);
  const dispatch = useDispatch();
  const { pathname: currentPath } = useLocation();
  useScrollToTop();

  useEffect(() => {
    const fetchMechanicDetails = async () => {
      try {
        const response = await getMechanicDetails();
        const {
          data: { mechanicDetails },
        } = response;
        dispatch(setMechanicData(mechanicDataMapper(mechanicDetails)));
      } catch (err) {
        console.error(err);
      }
    };
    const fetchCart = async () => {
      try {
        const response = await getCart();
        const {
          data: { cart },
        } = response;
        const { skuList, bundleList, cartSize } = cart;
        dispatch(setCart(cartDataMapper(skuList, bundleList, cartSize)));
      } catch (err) {
        console.error(err);
      }
    };
    const fetchCategories = async () => {
      try {
        const response = await getSkuCategories();
        const {
          data: { skuCategoryList },
        } = response;
        dispatch(setSkuCategories(skuCategoriesMapper(skuCategoryList)));
      } catch (err) {
        console.error(err);
      }
    };

    const fetchInitData = async () => {
      setIsInitDataLoading(true);
      await fetchMechanicDetails();
      if (currentPath !== APP_PATHS.CART.url) {
        await fetchCart();
      }
      await fetchCategories();
      setIsInitDataLoading(false);
    };

    if (isAuthenticated) {
      fetchInitData();
      amplitude.init(AMPLITUDE_API_KEY, userId, {
        defaultTracking: false,
        minIdLength: 1,
      });
    }
    // eslint-disable-next-line
  }, [dispatch, isAuthenticated]);

  return isInitDataLoading ? (
    <Loading className={classes["app__loading"]} />
  ) : (
    <Layout>
      <Router />
    </Layout>
  );
};

export default App;
